import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, pluck, includes, pick } from 'ramda';
import { Link } from 'react-router-dom';
import { truncate } from 'lodash';
import { createFragmentContainer, graphql, fetchQuery } from 'react-relay';

import { createRelayEnvironment } from '../../../../utils/relayHelpers';
import DatePresenter from '../../../../presenters/DatePresenter';
import StorePresenter from '../../../../presenters/StorePresenter';
import { useFetchStatus } from '../../../../utils/fetchStatusUtils';
import Button from '../../../../components/Button';

import StoresTable from './components/StoresTable';
import ChangeFastCashRateModal from './components/ChangeFastCashRateModal';

export const Stores = (props) => {
  const { stores, storesLoadingState, onRateUpdate, loadStores } = props;
  const { isFulfilled } = useFetchStatus(storesLoadingState);
  const storesCount = isFulfilled ? stores.length : 0;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAllStoresSelected, setIsAllStoreSelected] = useState(false);
  const [selectedStoresIds, setSelectedStoresIds] = useState([]);
  const [fastCashErrors, setFastCashErrors] = useState('');
  const environment = createRelayEnvironment();
  let checkRates = function (startDate, endDate, rateType) {
    return new Promise((resolve, reject) => {
      let result = true;
      selectedStoresIds.forEach(async (storeId) => {
        const variables = {
          storeIdEq: storeId,
          afterDate: startDate,
          beforeDate: (endDate = endDate || DatePresenter.today()),
        };
        await fetchQuery(
          environment,
          StoresCashbackRatesContainerQuery,
          variables
        ).then((data) => {
          // access the graphql response
          const defaultCashbackRates = data.brand.defaultCashbackRates;
          const birthdayCashbackRates = data.brand.birthdayCashbackRates;
          if (rateType === 'default') {
            for (const defaultRate in defaultCashbackRates) {
              if (
                defaultCashbackRates[defaultRate].startDate === startDate &&
                defaultCashbackRates[defaultRate].endDate === endDate
              ) {
                result = false;
              }
            }
          } else {
            for (const birthdayRate in birthdayCashbackRates) {
              if (
                birthdayCashbackRates[birthdayRate].startDate === startDate &&
                birthdayCashbackRates[birthdayRate].endDate === endDate
              ) {
                result = false;
              }
            }
          }
          if (result) {
            resolve('No Conflicts found');
          } else {
            reject('Conflicting rate with the same dates');
          }
        });
      });
    });
  };

  const handleStoreCheck = (storeId) => {
    const newStoresIds = includes(storeId, selectedStoresIds)
      ? removeId(storeId)
      : [...selectedStoresIds, storeId];

    setSelectedStoresIds(newStoresIds);
  };

  const removeId = (storeId) => {
    return selectedStoresIds.filter((id) => id !== storeId);
  };

  const handleCheckAllClick = () => {
    setIsAllStoreSelected(!isAllStoresSelected);
    const newStoresIds = isAllStoresSelected ? [] : pluck(['id'], stores);
    setSelectedStoresIds(newStoresIds);
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleRateUpdate = async (values, actions) => {
    setFastCashErrors('');
    const { isTemporaryRate } = values;
    const cashbackRateParams = isTemporaryRate
      ? pick(['startDate', 'endDate', 'rate', 'nextRate', 'rateType'], values)
      : pick(['startDate', 'rate', 'rateType'], values);
    const params = { ...cashbackRateParams, storeIds: selectedStoresIds };
    try {
      checkRates(
        cashbackRateParams.startDate,
        cashbackRateParams.endDate,
        cashbackRateParams.rateType
      )
        .then(async () => {
          await onRateUpdate(params);
          handleUpdateSuccess();
          actions.resetForm();
        })
        .catch((error) => {
          setFastCashErrors(error);
        });
    } catch (errors) {
      const {
        response: { data },
      } = errors;
      actions.setErrors(data);
    }
  };

  const handleUpdateSuccess = () => {
    resetSelectedStoresData();
    handleModalClose();
    loadStores();
  };

  const resetSelectedStoresData = () => {
    setSelectedStoresIds([]);
    setIsAllStoreSelected(false);
  };

  return (
    <div className="stores">
      <ChangeFastCashRateModal
        isModalOpen={isModalOpen}
        onSubmit={handleRateUpdate}
        onModalClose={handleModalClose}
        fastCashErrors={fastCashErrors}
        setFastCashErrors={setFastCashErrors}
      />
      <div className="header">
        <h2>
          Stores <span className="amount">({storesCount})</span>
        </h2>
        <div className="actions">
          <Button
            className="button"
            onClick={handleModalOpen}
            children="Change Rate"
            disabled={isEmpty(selectedStoresIds)}
          />
          <Link to="/brands/new" className="button">
            New Store
          </Link>
        </div>
      </div>
      <StoresTable
        stores={stores}
        loadingState={storesLoadingState}
        onStoreCheck={handleStoreCheck}
        onCheckAllClick={handleCheckAllClick}
        selectedStoresIds={selectedStoresIds}
        isAllStoresSelected={isAllStoresSelected}
      />
    </div>
  );
};
const StoresCashbackRatesContainerQuery = graphql`
  query StoresCashbackRatesContainerQuery(
    $storeIdEq: ID!
    $afterDate: Date!
    $beforeDate: Date!
  ) {
    brand: store(id: $storeIdEq) {
      id
      name
      defaultCashbackRates(afterDate: $afterDate, beforeDate: $beforeDate) {
        startDate
        endDate
        rate
        state
      }
      birthdayCashbackRates(afterDate: $afterDate, beforeDate: $beforeDate) {
        startDate
        endDate
        rate
        state
      }
    }
  }
`;

export default Stores;

Stores.propTypes = {
  stores: PropTypes.arrayOf(StorePresenter.shape()),
  storesLoadingState: PropTypes.string.isRequired,
  onRateUpdate: PropTypes.func.isRequired,
  loadStores: PropTypes.func.isRequired,
};

Stores.defaultProps = {
  stores: [],
};
